import { Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth/enterpriseAuth';
import { DashboardLayout } from './components/DashboardLayout';
import { LandingPage } from './components/LandingPage';
import { SignInModal } from './components/auth/SignInModal';
import { SignUpModal } from './components/auth/SignUpModal';
import './App.css';

const DashboardPage = lazy(() => import('./components/pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const APIReferencePage = lazy(() => import('./components/pages/APIReferencePage'));
const SDKsPage = lazy(() => import('./components/pages/SDKsPage'));
const IntegrationGuidesPage = lazy(() => import('./components/pages/IntegrationGuidesPage'));
const WebhookDocumentationPage = lazy(() => import('./components/pages/WebhookDocumentationPage'));
const DocumentationHubPage = lazy(() => import('./components/pages/DocumentationHubPage'));
const HostedOnboardingPage = lazy(() => import('./components/pages/HostedOnboardingPage'));
const QuickstartPage = lazy(() => import('./components/pages/QuickstartPage'));
const SandboxPage = lazy(() => import('./components/pages/SandboxPage'));
const ErrorCodesPage = lazy(() => import('./components/pages/ErrorCodesPage'));
const LoginPage = lazy(() => import('./components/pages/LoginPage'));
const DeveloperSecurityPage = lazy(() => import('./components/pages/DeveloperSecurityPage'));
const FaydaIntegrationPage = lazy(() => import('./components/pages/FaydaIntegrationPage'));
const SignUpPage = lazy(() => import('./components/pages/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('./components/pages/ForgotPasswordPage'));
const AccountPage = lazy(() => import('./components/pages/AccountPage'));
const APIKeysPage = lazy(() => import('./components/pages/APIKeysPage').then((module) => ({ default: module.APIKeysPage })));
const PortalPlaceholderPage = lazy(() => import('./components/pages/PortalPlaceholderPage').then((module) => ({ default: module.PortalPlaceholderPage })));
const AppsPage = lazy(() => import('./components/pages/AppsPage').then((module) => ({ default: module.AppsPage })));
const UsagePage = lazy(() => import('./components/pages/UsagePage').then((module) => ({ default: module.UsagePage })));
const DecisionControlPage = lazy(() => import('./components/pages/DecisionControlPage').then((module) => ({ default: module.DecisionControlPage })));
const DeveloperUtilityPage = lazy(() => import('./components/pages/DeveloperUtilityPage'));

function RequireAuth({ children }: { children: JSX.Element }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return children;
}

function RequirePermission({ permission, children }: { permission: string; children: JSX.Element }) {
  const { hasPermission } = useAuth();

  if (hasPermission(permission)) return children;

  return (
    <DashboardLayout>
      <main className="flex min-h-[60vh] items-center justify-center px-6" role="alert">
        <section className="max-w-lg text-center">
          <h1 className="text-xl font-semibold text-gray-950">Access restricted</h1>
          <p className="mt-2 text-sm text-gray-600">
            Your organization role does not grant <code>{permission}</code>. Ask an organization administrator to update your access.
          </p>
        </section>
      </main>
    </DashboardLayout>
  );
}

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

function AppContent() {
  const { user, isAuthenticated, signOut } = useAuth();
  const [showSignIn, setShowSignIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);

  const handleSignIn = () => setShowSignIn(true);
  const handleSignUp = () => setShowSignUp(true);
  
  const switchToSignUp = () => {
    setShowSignIn(false);
    setShowSignUp(true);
  };
  
  const switchToSignIn = () => {
    setShowSignUp(false);
    setShowSignIn(true);
  };

  const publicPageProps = {
    user,
    onSignIn: handleSignIn,
    onSignOut: () => {
      void signOut();
    },
    userRole: isAuthenticated ? user?.role ?? null : null,
  };

  return (
    <div className="App min-h-screen">
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage onSignIn={handleSignIn} onSignUp={handleSignUp} />} />
          <Route path="/quickstart" element={<QuickstartPage {...publicPageProps} />} />
          <Route path="/sandbox" element={<SandboxPage {...publicPageProps} />} />
          <Route path="/error-codes" element={<ErrorCodesPage {...publicPageProps} />} />
          <Route path="/login" element={<LoginPage {...publicPageProps} />} />
          <Route path="/signup" element={<SignUpPage {...publicPageProps} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage {...publicPageProps} />} />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <AccountPage {...publicPageProps} />
              </RequireAuth>
            }
          />
          <Route path="/docs" element={<DocumentationHubPage {...publicPageProps} />} />
          <Route path="/hosted-onboarding" element={<HostedOnboardingPage />} />
          <Route path="/api-reference" element={<APIReferencePage {...publicPageProps} />} />
          <Route path="/sdks" element={<SDKsPage {...publicPageProps} />} />
          <Route path="/integration-guides" element={<IntegrationGuidesPage {...publicPageProps} />} />
          <Route path="/webhooks" element={<WebhookDocumentationPage {...publicPageProps} />} />
          <Route path="/developers" element={<LandingPage onSignIn={handleSignIn} onSignUp={handleSignUp} />} />
          <Route path="/developers/docs" element={<DocumentationHubPage {...publicPageProps} />} />
          <Route path="/developers/quickstart" element={<QuickstartPage {...publicPageProps} />} />
          <Route path="/developers/api-reference" element={<APIReferencePage {...publicPageProps} />} />
          <Route path="/developers/sdks" element={<SDKsPage {...publicPageProps} />} />
          <Route path="/developers/webhooks" element={<WebhookDocumentationPage {...publicPageProps} />} />
          <Route path="/developers/sandbox" element={<SandboxPage {...publicPageProps} />} />
          <Route path="/developers/security" element={<DeveloperSecurityPage {...publicPageProps} />} />
          <Route path="/developers/fayda" element={<FaydaIntegrationPage {...publicPageProps} />} />
          <Route path="/integration-guides/fayda" element={<FaydaIntegrationPage {...publicPageProps} />} />
          <Route path="/developers/postman" element={<DeveloperUtilityPage {...publicPageProps} kind="postman" />} />
          <Route path="/developers/status" element={<DeveloperUtilityPage {...publicPageProps} kind="status" />} />
          <Route path="/developers/support" element={<DeveloperUtilityPage {...publicPageProps} kind="support" />} />

          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <DashboardLayout>
                  <DashboardPage />
                </DashboardLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/api-keys"
            element={
              <RequireAuth>
                <RequirePermission permission="api-keys:manage">
                  <DashboardLayout><APIKeysPage /></DashboardLayout>
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <DashboardLayout>
                  <PortalPlaceholderPage title="Profile" description="Profile management will hold developer identity details, role assignments, and activity preferences." />
                </DashboardLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <RequirePermission permission="settings:manage"><DashboardLayout>
                  <PortalPlaceholderPage title="Settings" description="Settings will become the home for organization defaults, webhook destinations, environment rules, and notification preferences." />
                </DashboardLayout></RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/security"
            element={
              <RequireAuth>
                <RequirePermission permission="settings:manage"><DashboardLayout>
                  <PortalPlaceholderPage title="Security" description="Security settings will cover session governance, key rotation visibility, and access-control policy checkpoints for developer teams." />
                </DashboardLayout></RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/apps"
            element={
              <RequireAuth>
                <RequirePermission permission="api-keys:manage">
                  <DashboardLayout><AppsPage /></DashboardLayout>
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/decision-control"
            element={
              <RequireAuth>
                <RequirePermission permission="decision-control:manage">
                  <DashboardLayout><DecisionControlPage /></DashboardLayout>
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/logs"
            element={
              <RequireAuth>
                <RequirePermission permission="analytics:view"><DashboardLayout>
                  <PortalPlaceholderPage title="Logs" description="Logs will surface request and webhook delivery history with status, latency, payload inspection, and retry controls." />
                </DashboardLayout></RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/usage"
            element={
              <RequireAuth>
                <RequirePermission permission="analytics:view">
                  <DashboardLayout><UsagePage /></DashboardLayout>
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/rate-limits"
            element={
              <RequireAuth>
                <RequirePermission permission="api-keys:manage"><DashboardLayout>
                  <PortalPlaceholderPage title="Rate Limits" description="Rate limits will show current per-key and per-tenant ceilings, recent throttling events, and headroom before the next limit." />
                </DashboardLayout></RequirePermission>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      
      <SignInModal
        open={showSignIn}
        onClose={() => setShowSignIn(false)}
        onSwitchToSignUp={switchToSignUp}
      />
      
      <SignUpModal
        open={showSignUp}
        onClose={() => setShowSignUp(false)}
        onSwitchToSignIn={switchToSignIn}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
